// Sawyer 09 pet-inactivity (D1)
/**
 * "Inactive since Jun 9, 2099 · last visit Dec 9, 2098" under the dog
 * profile's safety band.
 *
 * An info Alert, not the danger band: inactivity is not a safety fact, it is
 * the desk's cue that a returning dog needs a new meet and greet before the
 * owner asks why the booking is held. The second line appears only when the
 * server says the hold applies (`needsMeetAndGreet`: the tenant's assessment
 * schedule is configured), so the profile never promises a hold the booking
 * gate will not apply (Design Philosophy #5).
 *
 * Renders nothing for an active dog, a rule-off tenant, a failed read
 * (`null`) or an older server that sends no `inactivity` (deploy window).
 * Dates are calendar dates (no timezone shift; the #1156 lesson).
 *
 * Counter Test: Kylie opens Biscuit's profile with his owner on the phone,
 * reads "Inactive since Jun 9, 2099 · last visit Dec 9, 2098" and books the
 * meet and greet first.
 */
import { Alert } from '@ferro/ui';
import type { PetInactivityView } from '../../lib/api.js';
import { calendarDateLabel } from '../reports/inactive-since.js';
import '../reports/inactive-dogs-toggle.css';

export function petInactivityHeadline(v: Extract<PetInactivityView, { state: 'inactive' }>): string {
  const since = calendarDateLabel(v.inactiveSince) ?? v.inactiveSince;
  if (v.lastVisitOn) {
    return `Inactive since ${since} · last visit ${calendarDateLabel(v.lastVisitOn) ?? v.lastVisitOn}`;
  }
  return `Inactive since ${since} · no visit on record, counted from when the pet was added (${
    calendarDateLabel(v.addedOn) ?? v.addedOn
  })`;
}

export function PetInactivityLine({ inactivity }: { inactivity: PetInactivityView | null | undefined }): JSX.Element | null {
  if (!inactivity || inactivity.rule !== 'on' || inactivity.state !== 'inactive') return null;
  return (
    <div className="ferro-inactivity-line" data-testid="dogprofile-inactivity">
      <Alert variant="info">
        <p data-testid="dogprofile-inactivity-headline">{petInactivityHeadline(inactivity)}</p>
        {inactivity.needsMeetAndGreet ? (
          <p data-testid="dogprofile-inactivity-hold">Needs a new meet and greet before daycare or boarding.</p>
        ) : null}
      </Alert>
    </div>
  );
}
